import React, { useEffect, useState, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Row, Col } from 'react-bootstrap';
import { withRouter, Route, Switch } from 'react-router-dom';
import { Select, Button, Loader } from 'sa-ui';
import queryString from 'query-string';

import * as actions from '../../reportsActions';
import * as detailsActions from '../reportApplicationDetailsActions';

import messages from '../reportApplicationDetailsMessages';

import Chart from '../../ReportBarChart';
import GradeCurricularComponentTable from './StudentsGradeCurricularComponentTable';
import { promisify } from '../../../app/actions';

import reportMessages from '../../reportsMessages';
import * as helpers from '../../reportHelpers';

import '../../ReportsCommon.scss';

function getInitialRankingLimit() {
  const params = queryString.parse(window.location.search.replace('?', ''));
  return params.rankingLimit ? Number(params.rankingLimit) : 10;
}

const ReportApplicationComponentsContainer = props => {
  const [filter, setFilter] = useState(queryString.parse(window.location.search.replace('?', '')));
  const [rankingLimit, setRankingLimit] = useState(getInitialRankingLimit);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    props.getGroups();
  }, []);

  async function selectFilter(idCSchool, idESCourse, idESUser) {
    setFilter({ ...filter, idCSchool });
    setLoading(true);

    try {
      await props.getESFiltersComponents({
        idEvaluation: props.idEvaluation,
        idCSchool,
        idESCourse,
      });

      await props.getESGradeByDiscipline({
        idEvaluation: props.idEvaluation,
        idCSchool,
        idESCourse,
        idESUser,
      });

      await props.getESQuestionsDetailsByComponents({
        idEvaluation: props.idEvaluation,
        idCSchool,
        idESCourse,
        idESUser,
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    selectFilter(filter.idCSchool, filter.idESCourse, filter.idESUser);
  }, [filter.idCSchool, filter.idESCourse, filter.idESUser]);

  useEffect(() => {
    if (props.filtersComponents) {
      if (props.filtersComponents.length === 1) {
        setFilter({
          ...filter,
          idCSchool: props.filtersComponents[0].idCSchool,
        });
      }
    }
  }, [props.filtersComponents]);

  function clearFilters() {
    setFilter({
      idCSchool: undefined,
      idESCourse: undefined,
      idESUser: undefined,
    });
  }

  function handleChangeSchool(event) {
    setFilter({
      ...filter,
      idCSchool: event.target.value,
      idESCourse: undefined,
      idESUser: undefined,
    });
  }

  function handleChangeCourse(event) {
    setFilter({
      ...filter,
      idESCourse: event.target.value,
      idESUser: undefined,
    });
  }

  function handleChangeStudent(event) {
    setFilter({
      ...filter,
      idESUser: event.target.value,
    });
  }

  const schoolOptions = helpers.getFASchoolOptions(props.filtersComponents.schools);
  const classesOptions = helpers.getFComponentsClassesOptions(props.filtersComponents.courses);
  const studentsOptions = helpers.getFComponentsStudentsOptions(props.filtersComponents.students);

  useImperativeHandle(props.forwardRef, () => ({
    getFilter: () => filter,
  }));

  useEffect(() => {
    if (props.gradeByDiscipline) {
      setTimeout(() => window.dispatchEvent(new CustomEvent('app-ready')));
    }
  }, [props.gradeByDiscipline]);

  return (
    <div>
      <Loader show={loading} />
      <h6 className="filter subtitle mt-3">
        <i className="pe-7s-filter" />
        <FormattedMessage {...messages.filters} />
        <FormattedMessage {...messages.clear}>
          {txt => (
            <button className="btn btn-primary btn-sm ml-3" onClick={clearFilters}>
              {txt}
            </button>
          )}
        </FormattedMessage>
      </h6>
      <Row className="align-items-center">
        <Col md={4}>
          <Select
            containerClassName="mt-0"
            nullable
            name="idCSchool"
            onChange={handleChangeSchool}
            value={filter.idCSchool}
            options={schoolOptions}
            placeholder={props.intl.formatMessage(reportMessages.school)}
          />
        </Col>
        <Col md={4}>
          <Select
            containerClassName="mt-0"
            disabled={!filter.idCSchool}
            nullable
            name="idCourse"
            value={filter.idESCourse}
            onChange={handleChangeCourse}
            options={classesOptions}
            placeholder={props.intl.formatMessage(reportMessages.class)}
          />
        </Col>
        <Col md={4}>
          <Select
            containerClassName="mt-0"
            disabled={!filter.idESCourse}
            nullable
            name="idESUser"
            value={filter.idESUser}
            onChange={handleChangeStudent}
            options={studentsOptions}
            placeholder={props.intl.formatMessage(reportMessages.student)}
          />
        </Col>
      </Row>
      <div id="divToPDF" className="divToPDF">
        {props.gradeByDiscipline && (
          <React.Fragment>
            <Row className="mt-3">
              <Col md={12}>
                <Chart
                  schools={props.gradeByDiscipline}
                  title={props.intl.formatMessage(messages.generalPerformanceByComponent)}
                />
              </Col>
            </Row>
          </React.Fragment>
        )}
        <GradeCurricularComponentTable data={props.questionsComponent} limit={rankingLimit} />
      </div>
    </div>
  );
};

ReportApplicationComponentsContainer.propTypes = {};

const mapStateToProps = ({ reports, reportApplicationDetails, app }, { match }) => ({
  ...reports,
  ...reportApplicationDetails,
  ...match.params,
  permission: app.permission,
});

export default compose(
  injectIntl,
  withRouter,
  connect(mapStateToProps, promisify({ ...actions, ...detailsActions })),
)(ReportApplicationComponentsContainer);
